import React, { useState, ChangeEvent, FormEvent } from 'react';
import { useSelector } from 'react-redux';
import {
    TextField,
    Button,
    Grid,
    FormControl,
    Container,
} from '@mui/material';
import { Autocomplete } from '@mui/material'; // Import Autocomplete component
import { RootState } from '../../../store/store';
import Api, { APIs } from '../../../Api';
import CommonUtils from '../../base/utils/CommonUtils';

// Define the shape of the form data
interface FormData {
    name: string;
    phone: string;
    region: string;
    sector: string;
    file: File | null;
}
interface  IProps{
    handleResult: any
}

const UploadConceptPaperForm = (props:IProps) => {
    // Get the user's phone number and manageInfoId from Redux
    const phoneNo: string = useSelector((state: RootState) => state.auth.phoneNo) || '923224785104';
    const manageInfoId: string = useSelector((state: RootState) => state.auth.manageInfoId) || '5';

    // Form state
    const [formData, setFormData] = useState<FormData>({
        name: '',
        phone: phoneNo || '',
        region: '',
        sector: '',
        file: null,
    });

    // Available options for region and sector
    const [regions, setRegions] = useState<string[]>([]);
    const [sectors, setSectors] = useState<string[]>([]);

    // Fetch unique region and sector data on component mount
    React.useEffect(() => {
        Api.get(APIs.MoPD_UNIQUE_LIST).then((payload) => {
            if (payload) {
                setRegions(payload.region);
                setSectors(payload.sector_name);
            }
        });
    }, []);

    // Handle text or select input change
    const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormData((prevFormData) => ({
            ...prevFormData,
            [name]: value,
        }));
    };

    // Handle file change for PDF upload
    const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file && file.type === 'application/pdf') {
            setFormData((prevFormData) => ({
                ...prevFormData,
                file,
            }));
        } else {
            alert('Please upload a PDF file');
        }
    };

    // Handle form submission
    const handleSubmit = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        // Create a FormData object to handle file and form data
        const data = new FormData();
        data.append('name', formData.name);
        data.append('phone', formData.phone);
        data.append('region', formData.region);
        data.append('sector', formData.sector);

        // Check if a file is selected and append it
        if (formData.file) {
            data.append('file', formData.file);
        }
        CommonUtils.showSnackbar('Uploading a file, please waiting');
        // Send the FormData object via API call
        Api.postFile(APIs.MoPD_UPLOAD_CONCEPT_FILE, data, { manage_info_id: manageInfoId })
            .then((payload) => {
                if (payload) {
                    console.log("concept_paper", payload)
                    props.handleResult(payload.concept_paper)
                    CommonUtils.showSnackbar('File uploaded successfully');
                }
            })
            .catch((error) => {
                console.error('Error uploading file:', error);
                CommonUtils.showSnackbar('Failed to upload the file');
            });
    };

    return (
        <Container sx={{ alignItems: 'center', textAlign: 'center', paddingTop: '20px' }}>
            <form onSubmit={handleSubmit}>
                <Grid container spacing={2}>
                    {/* User Name Field */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="User Name"
                            name="name"
                            value={formData.name}
                            onChange={handleChange}
                            required
                        />
                    </Grid>

                    {/* Phone Number Field (Pre-filled) */}
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Phone Number"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            disabled
                        />
                    </Grid>

                    {/* Region Autocomplete */}
                    <Grid item xs={12}>
                        <FormControl fullWidth required>
                            <Autocomplete
                                options={regions}
                                getOptionLabel={(option) => option}
                                value={formData.region}
                                onChange={(event, newValue) => {
                                    setFormData((prevFormData) => ({
                                        ...prevFormData,
                                        region: newValue || '',
                                    }));
                                }}
                                renderInput={(params) => <TextField {...params} label="Region" />}
                            />
                        </FormControl>
                    </Grid>

                    {/* Sector Autocomplete */}
                    <Grid item xs={12}>
                        <FormControl fullWidth required>
                            <Autocomplete
                                options={sectors}
                                getOptionLabel={(option) => option}
                                value={formData.sector}
                                onChange={(event, newValue) => {
                                    setFormData((prevFormData) => ({
                                        ...prevFormData,
                                        sector: newValue || '',
                                    }));
                                }}
                                renderInput={(params) => <TextField {...params} label="Sector" />}
                            />
                        </FormControl>
                    </Grid>

                    {/* File Upload */}
                    <Grid item xs={12}>
                        <Button variant="contained" color="secondary" component="label" fullWidth={true}>
                            Upload PDF Concept Paper
                            <input
                                type="file"
                                accept="application/pdf"
                                hidden
                                onChange={handleFileChange}
                            />
                        </Button>
                        {formData.file && <p>{formData.file.name}</p>}
                    </Grid>

                    {/* Submit Button */}
                    <Grid item xs={12}>
                        <Button type="submit" variant="contained" color="primary">
                            Submit
                        </Button>
                    </Grid>
                </Grid>
            </form>
        </Container>
    );
};

export default UploadConceptPaperForm;
